<template>
    <div>
        <ValidationObserver v-slot="{ dirty }" ref="observer">
            <form class="grid grid-cols-2">
                <app-fieldset class="col-span-2 mt-5" :label="$t('invoices.reviewsTitle')">
                    <div class="flex flex-col w-full col-span-2 gap-2">
                        <div>
                            <app-button
                                :label="$t('invoices.reviews')"
                                @click="$refs.multiBundlePicker.open()"
                            ></app-button>
                        </div>
                        <table class="w-full border text-xs" v-if="invoiceReviews.length > 0">
                            <tr class="border-b">
                                <th class="border p-2 text-left">{{ $t('invoices.reviews') }}</th>
                                <th class="border p-2 text-center">{{ $t('commons.emissionDueDate') }}</th>
                                <th class="border p-2 text-center">{{ $t('invoices.emissionDate') }}</th>
                                <th class="border p-2 text-right">{{ $t('invoices.conclusion') }}</th>
                                <th class="border p-2 text-right">{{ $t('invoices.comment') }}</th>
                            </tr>
                            <tr class="border-b" v-for="invoiceReview in invoiceReviews">
                                <td class="border p-2">
                                    <span>{{ invoiceReview.emitter.label }}</span>
                                </td>
                                <td class="border p-2">
                                    <div class="flex justify-center">
                                        <app-date-link
                                            :label="$t('commons.emissionDueDate')"
                                            :show-label="false"
                                            v-model="invoiceReview.emissionDueDate"
                                            @input="saveReview(invoiceReview)"
                                            class="underline"
                                            :disabled="!isAdmin"
                                        />
                                    </div>
                                </td>
                                <td class="border p-2">
                                    <div class="flex justify-center">
                                        <app-date-link
                                            :label="$t('invoices.emissionDate')"
                                            :show-label="false"
                                            v-model="invoiceReview.emissionDate"
                                            @input="saveReview(invoiceReview)"
                                            class="underline"
                                            :disabled="
                                                !isAdmin && !project.me.bundleIds.includes(invoiceReview.emitterId)
                                            "
                                        />
                                    </div>
                                </td>
                                <td class="border p-2 text-right">
                                    <app-select
                                        v-model="invoiceReview.conclusion"
                                        :label="$t('invoices.conclusion')"
                                        @input="saveReview(invoiceReview)"
                                        :show-label="false"
                                        :disabled="!isAdmin && !project.me.bundleIds.includes(invoiceReview.emitterId)"
                                    >
                                        <option value=""></option>
                                        <option value="validated">{{ $t('invoices.status.validated') }}</option>
                                        <option value="rejected">{{ $t('invoices.status.rejected') }}</option>
                                    </app-select>
                                </td>
                                <td class="border p-2 text-right">
                                    <app-textarea
                                        v-model="invoiceReview.comment"
                                        @blur="saveReview(invoiceReview)"
                                        :disabled="!isAdmin && !project.me.bundleIds.includes(invoiceReview.emitterId)"
                                    ></app-textarea>
                                </td>
                            </tr>
                        </table>
                    </div>
                </app-fieldset>
                <app-fieldset class="col-span-2 mt-5" :label="$t('invoices.conclusionMOE')">
                    <app-select
                        v-model="invoice.conclusion"
                        :label="$t('invoices.conclusion')"
                        @input="save()"
                        :disabled="!isAdmin"
                    >
                        <option value=""></option>
                        <option value="validated">{{ $t('invoices.status.validated') }}</option>
                        <option value="validatedWithComments">{{ $t('invoices.status.validatedWithComments') }}</option>
                        <option value="rejected">{{ $t('invoices.status.rejected') }}</option>
                        <option value="obsolete">{{ $t('invoices.status.obsolete') }}</option>
                    </app-select>
                    <app-date-input
                        v-model="invoice.conclusionDate"
                        :label="$t('invoices.conclusionDate')"
                        @input="save()"
                        :disabled="!isAdmin"
                    ></app-date-input>
                    <app-label
                        :label="$t('invoices.invoiceComment')"
                        v-if="
                            invoice.conclusion === 'rejected' ||
                            invoice.conclusion === 'obsolete' ||
                            invoice.conclusion === 'validatedWithComments'
                        "
                        :disabled="!isAdmin"
                    >
                        <app-textarea v-model="invoice.invoiceComment" @blur="save()"></app-textarea>
                    </app-label>
                </app-fieldset>

                <app-save-on-leave :dirty="dirty" :saveFn="() => save(true)"></app-save-on-leave>
            </form>
        </ValidationObserver>
        <app-multi-picker
            ref="multiBundlePicker"
            class="col-span-2"
            variant="hidden"
            :show-label="false"
            v-model="selectedBundles"
            :options="bundles"
            :title="$t('invoices.chooseReviewers')"
            @close="validateReviewers"
        >
            <template v-slot:option="{ option }">
                <app-bundle :bundle="option"></app-bundle>
            </template>
            <template v-slot:footer>
                <div class="p-4 flex justify-end">
                    <app-button
                        @click="$refs.multiBundlePicker.close()"
                        variant="primary"
                        :label="$t('commons.save')"
                    ></app-button>
                </div>
            </template>
        </app-multi-picker>
    </div>
</template>

<script>
import { getMapById } from '@/services/sanitize.service';
import { getInvoice, updateInvoice } from './invoices.service';
import { combineLatest } from 'rxjs';
import { getBundles } from '@/features/bundles/bundles.service';
import AppLabel from '@/components/appLabel/AppLabel.vue';
import { getCompanies } from '@/features/companies/companies.service';
import AppTextarea from '@/components/app-textarea/AppTextarea.vue';
import { queryProject } from '@/features/projects/projects.service';
import AppDateLink from '@/components/appDateLink/AppDateLink.vue';
import {
    createInvoiceReviews,
    getInvoiceReviews,
    removeInvoiceReviews,
    updateInvoiceReview,
} from '@/features/invoices/invoiceReviews.service';
import AppMultiPicker from '@/components/appMultiPicker/AppMultiPicker.vue';
import AppFieldset from '@/components/appFieldset/AppFieldset.vue';
import AppButton from '@/components/appButton/AppButton.vue';
import AppSelect from '@/components/appSelect/AppSelect.vue';
import AppDateInput from '@/components/appDateInput/AppDateInput.vue';
import AppSaveOnLeave from '@/components/AppSaveOnLeave.vue';
import AppBundle from '@/components/app-bundle/appBundle.vue';

export default {
    components: {
        AppBundle,
        AppTextarea,
        AppMultiPicker,
        AppSaveOnLeave,
        AppLabel,
        AppDateInput,
        AppSelect,
        AppDateLink,
        AppButton,
        AppFieldset,
    },
    async created() {
        queryProject(this.$route.params.projectId).then((project) => {
            this.isAdmin = project.me.allowedFeatures.includes('project_invoices');
            this.project = project;
        });
        this.init();
    },
    watch: {
        $route() {
            this.init();
        },
    },
    methods: {
        init() {
            this.subscriptions = [
                combineLatest([
                    getBundles(this.$route.params.projectId),
                    getCompanies(this.$route.params.projectId),
                    getInvoice(this.$route.params.projectId, this.$route.params.invoiceId),
                    getInvoiceReviews(this.$route.params.projectId, this.$route.params.invoiceId),
                ]).subscribe(async ([bundles, companies, invoice, invoiceReviews]) => {
                    const companyMap = getMapById(companies);
                    const mappedBundles = bundles.map((bundle) => ({
                        ...bundle,
                        company: companyMap[bundle.companyId],
                    }));
                    const bundleMap = getMapById(mappedBundles);
                    this.invoice = invoice;
                    this.bundles = mappedBundles;
                    this.invoiceReviews = invoiceReviews.map((invoiceReview) => ({
                        ...invoiceReview,
                        emitter: bundleMap[invoiceReview.emitterId],
                    }));
                    this.selectedBundles = this.invoiceReviews
                        .map((invoiceReview) => invoiceReview.emitter)
                        .filter((a) => !!a);
                }),
            ];
        },
        async validateReviewers() {
            const bundlesToAdd = this.selectedBundles.filter(
                (bundle) => !this.invoiceReviews.find((invoiceReview) => invoiceReview.emitterId === bundle.id),
            );
            const reviewersToRemove = this.invoiceReviews.filter(
                (invoiceReview) => !this.selectedBundles.find((bundle) => invoiceReview.emitterId === bundle.id),
            );
            await Promise.all(
                bundlesToAdd.map((bundle) =>
                    createInvoiceReviews(this.$route.params.projectId, this.invoice.id, { emitterId: bundle.id }),
                ),
            );
            await Promise.all(
                reviewersToRemove.map((invoiceReview) =>
                    removeInvoiceReviews(this.$route.params.projectId, invoiceReview.id),
                ),
            );
        },
        async saveReview(invoiceReview) {
            await updateInvoiceReview(this.$route.params.projectId, {
                id: invoiceReview.id,
                comment: invoiceReview.comment,
                emissionDate: invoiceReview.emissionDate,
                conclusion: invoiceReview.conclusion,
                emissionDueDate: invoiceReview.emissionDueDate,
            });
        },
        async save() {
            await updateInvoice(this.$route.params.projectId, {
                id: this.invoice.id,
                conclusionDate: this.invoice.conclusionDate,
                conclusion: this.invoice.conclusion,
                invoiceComment: this.invoice.invoiceComment,
            });
        },
    },
    data() {
        return {
            selectedBundles: [],
            invoiceReviews: [],
            subscriptions: [],
            bundles: [],
            invoice: {},
            isAdmin: false,
            project: { me: { bundleIds: [] } },
        };
    },
};
</script>
